import { useCallback, useEffect, useMemo, useState } from "react"
import { Layer, Source, useMap } from "react-map-gl/mapbox"
import { debounce } from "lodash"
import type { FeatureCollection } from "geojson"
import type { ICommune, IParcel } from "@/types/explore"

interface SearchParcelsLayerProps {
  opacity?: number
  filter: {
    minParcelSurface?: number
    maxParcelSurface?: number
    ownerTypes?: string[]
    siren?: string
  }
  commune?: ICommune
  onDataLoaded?: (features: IParcel[]) => void
}

export const SearchParcelsLayer = ({ opacity, filter, commune, onDataLoaded }: SearchParcelsLayerProps) => {
  const { current: map } = useMap()
  const [geoJsonData, setGeoJsonData] = useState<FeatureCollection>({ type: "FeatureCollection", features: [] })

  const loadData = useCallback(async () => {
    try {
      if (!commune?.code) return

      const bbox = map?.getBounds()
      const params = new URLSearchParams()
      params.set("commune", commune.code)
      if (filter.minParcelSurface) params.set("minSurface", String(filter.minParcelSurface))
      if (filter.maxParcelSurface) params.set("maxSurface", String(filter.maxParcelSurface))
      if (filter.ownerTypes?.length) params.set("ownerTypes", filter.ownerTypes.join(","))
      if (filter.siren && filter.siren.length === 9) params.set("siren", filter.siren)
      if (bbox) {
        params.set("bbox", `${bbox.getWest()},${bbox.getSouth()},${bbox.getEast()},${bbox.getNorth()}`)
      }

      const res = await fetch(`/api/parcels/search?${params.toString()}`)
      if (!res.ok) return

      const data = await res.json()
      setGeoJsonData(data)
      onDataLoaded?.(data.features || [])
    } catch (err) {
      console.error("Error loading parcel data:", err)
    }
  }, [filter, filter.minParcelSurface, filter.maxParcelSurface, map, commune, onDataLoaded])

  useEffect(() => {
    debouncedLoadData()
  }, [
    filter.minParcelSurface,
    filter.maxParcelSurface,
    filter.ownerTypes?.join(","),
    filter.siren?.length === 9 ? filter.siren : "",
  ])

  const debouncedLoadData = useMemo(
    () =>
      debounce(() => {
        loadData()
      }, 800),
    [loadData],
  )

  useEffect(() => {
    if (!map) return

    const handleMoveEnd = () => {
      debouncedLoadData()
    }

    map.on("moveend", handleMoveEnd)

    return () => {
      map.off("moveend", handleMoveEnd)
      debouncedLoadData.cancel()
    }
  }, [map, debouncedLoadData])

  return (
    <Source promoteId="idu" id="search-parcels" type="geojson" data={geoJsonData}>
      <Layer
        minzoom={10}
        id="search-parcels"
        type="fill"
        paint={{
          "fill-color": ["case", ["==", ["get", "hasOwner"], true], "#46A097", "#1E40AF"],
          "fill-opacity": [
            "case",
            ["boolean", ["feature-state", "select"], false],
            1 * (opacity ?? 1),
            0.5 * (opacity ?? 1),
          ],
        }}
      />
      <Layer
        minzoom={10}
        id="search-parcels-border"
        type="line"
        paint={{
          "line-color": ["case", ["==", ["get", "isFavourite"], true], "#FC814F", "#46A097"],
          "line-width": 1,
        }}
      />
      <Layer
        minzoom={14}
        id="search-parcels-label"
        type="symbol"
        layout={{
          "text-field": ["concat", ["get", "section"], " ", ["get", "numero"]],
          "text-size": 12,
          "text-allow-overlap": false,
        }}
        paint={{
          "text-color": "#000000",
          "text-halo-color": "#ffffff",
          "text-halo-width": 1.5,
        }}
      />
    </Source>
  )
}
